// src/features/dashboard/mocks/Employee/employeeCompliance.mock.ts

// import type {
//   BulkImportResponse,
// } from '../../types/Employee/employeeCompliance.types';

import type {
  CompliancePageResponse,
} from '../../types/Employee/employeeCompliance.types';

/**
 * IMPORTANT
 * ---------------------------------------------------------
 * This file contains synthetic data ONLY.
 *
 * Do NOT add:
 * - Real employee names
 * - Real employee codes
 * - Real email addresses
 * - Real NTIDs
 * - Real manager names
 * - Real organization-sensitive information
 *
 * This file is intended only for local/mock development.
 *
 * STG / Production must retrieve real employee information
 * from the authenticated .NET API.
 * ---------------------------------------------------------
 */

// ======================================================
// Compliance Page Mock
// ======================================================

export const employeeComplianceMock: CompliancePageResponse = {
  // ====================================================
  // Header
  // ====================================================

  header: {
    title: 'Compliance',

    gatesMet: 4,

    totalGates: 4,

    badge: {
      employeeName: 'Test Employee',

      login: 'Login',

      breaks: 'Breaks',

      hours: 'Hours',

      policy: 'Policy',

      week: 'WTD 04 May 2026',
    },
  },

  // ====================================================
  // Compliance KPI Cards
  // ====================================================

  cards: [
    {
      id: 1,

      title: 'Login Compliance',

      status: 'Met',

      description: '09:02 · window 08:45–09:15',

      subDescription: '',

      iconBgColor: '#E8F8F2',

      iconColor: '#2DBE8D',

      statusColor: '#15803D',
    },

    {
      id: 2,

      title: 'Break Compliance',

      status: 'Met',

      description: '3 / 4 allowed · within policy',

      subDescription: '',

      iconBgColor: '#E8F1FE',

      iconColor: '#3B82F6',

      statusColor: '#15803D',
    },

    {
      id: 3,

      title: 'Hours Compliance',

      status: 'Met',

      description: '8h 32m of 8h req. (in progress)',

      subDescription: '',

      iconBgColor: '#F2EBFF',

      iconColor: '#7C3AED',

      statusColor: '#15803D',
    },

    {
      id: 4,

      title: 'Policy Compliance',

      status: 'Met',

      description: 'No restricted apps detected',

      subDescription: '',

      iconBgColor: '#FCE7F3',

      iconColor: '#DB2777',

      statusColor: '#15803D',
    },
  ],

  // ====================================================
  // Login Chart
  // ====================================================

  loginChart: [
    {
      day: 'Mon 28',
      loginTime: '09:01',
      buffer: 14,
    },
    {
      day: 'Tue 29',
      loginTime: '09:03',
      buffer: 12,
    },
    {
      day: 'Wed 30',
      loginTime: '09:00',
      buffer: 15,
    },
    {
      day: 'Thu 01',
      loginTime: '09:05',
      buffer: 10,
    },
    {
      day: 'Fri 02',
      loginTime: '08:59',
      buffer: 16,
    },
    {
      day: 'Mon 04',
      loginTime: '09:02',
      buffer: 13,
    },
  ],

  // ====================================================
  // Login Table
  // ====================================================

  loginTable: [
    {
      day: 'Mon 28',
      loginTime: '09:01',
      window: '08:45–09:15',
      status: 'Met',
    },
    {
      day: 'Tue 29',
      loginTime: '09:03',
      window: '08:45–09:15',
      status: 'Met',
    },
    {
      day: 'Wed 30',
      loginTime: '09:00',
      window: '08:45–09:15',
      status: 'Met',
    },
    {
      day: 'Thu 01',
      loginTime: '09:05',
      window: '08:45–09:15',
      status: 'Met',
    },
    {
      day: 'Fri 02',
      loginTime: '08:59',
      window: '08:45–09:15',
      status: 'Met',
    },
    {
      day: 'Mon 04',
      loginTime: '09:02',
      window: '08:45–09:15',
      status: 'Met',
    },
  ],

  // ====================================================
  // Break Chart
  // ====================================================

  breakChart: [
    {
      day: 'Mon 28',
      breaks: 3,
      totalMinutes: 46,
    },
    {
      day: 'Tue 29',
      breaks: 2,
      totalMinutes: 39,
    },
    {
      day: 'Wed 30',
      breaks: 4,
      totalMinutes: 58,
    },
    {
      day: 'Thu 01',
      breaks: 3,
      totalMinutes: 42,
    },
    {
      day: 'Fri 02',
      breaks: 2,
      totalMinutes: 36,
    },
    {
      day: 'Mon 04',
      breaks: 3,
      totalMinutes: 41,
    },
  ],

  // ====================================================
  // Break Table
  // ====================================================

  breakTable: [
    {
      day: 'Mon 28',
      breaks: 3,
      total: '46 min',
      status: 'Met',
    },
    {
      day: 'Tue 29',
      breaks: 2,
      total: '39 min',
      status: 'Met',
    },
    {
      day: 'Wed 30',
      breaks: 4,
      total: '58 min',
      status: 'Met',
    },
    {
      day: 'Thu 01',
      breaks: 3,
      total: '42 min',
      status: 'Met',
    },
    {
      day: 'Fri 02',
      breaks: 2,
      total: '36 min',
      status: 'Met',
    },
    {
      day: 'Mon 04',
      breaks: 3,
      total: '41 min',
      status: 'Met',
    },
  ],

  // ====================================================
  // Hours Chart
  // ====================================================

  hoursChart: [
    {
      day: 'Mon 28',
      loggedHours: 8.4,
      requiredHours: 8,
    },
    {
      day: 'Tue 29',
      loggedHours: 8.1,
      requiredHours: 8,
    },
    {
      day: 'Wed 30',
      loggedHours: 8.6,
      requiredHours: 8,
    },
    {
      day: 'Thu 01',
      loggedHours: 8.3,
      requiredHours: 8,
    },
    {
      day: 'Fri 02',
      loggedHours: 8.2,
      requiredHours: 8,
    },
    {
      day: 'Mon 04',
      loggedHours: 8.5,
      requiredHours: 8,
    },
  ],

  // ====================================================
  // Hours Table
  // ====================================================

  hoursTable: [
    {
      day: 'Mon 28',
      logged: '8h 24m',
      required: '8h',
      status: 'Met',
    },
    {
      day: 'Tue 29',
      logged: '8h 06m',
      required: '8h',
      status: 'Met',
    },
    {
      day: 'Wed 30',
      logged: '8h 36m',
      required: '8h',
      status: 'Met',
    },
    {
      day: 'Thu 01',
      logged: '8h 18m',
      required: '8h',
      status: 'Met',
    },
    {
      day: 'Fri 02',
      logged: '8h 12m',
      required: '8h',
      status: 'Met',
    },
    {
      day: 'Mon 04',
      logged: '8h 30m',
      required: '8h',
      status: 'Met',
    },
  ],

  // ====================================================
  // Policy Chart
  // ====================================================

  policyChart: [
    {
      day: 'Mon 28',
      minutes: 28,
      threshold: 30,
    },
    {
      day: 'Tue 29',
      minutes: 18,
      threshold: 30,
    },
    {
      day: 'Wed 30',
      minutes: 15,
      threshold: 30,
    },
    {
      day: 'Thu 01',
      minutes: 35,
      threshold: 30,
    },
    {
      day: 'Fri 02',
      minutes: 12,
      threshold: 30,
    },
    {
      day: 'Mon 04',
      minutes: 22,
      threshold: 30,
    },
  ],

  // ====================================================
  // Policy Table
  // ====================================================

  policyTable: [
    {
      day: 'Mon 28',
      safe: '5',
      violations: '0',
      status: 'Met',
    },
    {
      day: 'Tue 29',
      safe: '5',
      violations: '0',
      status: 'Met',
    },
    {
      day: 'Wed 30',
      safe: '5',
      violations: '1',
      status: 'Missed',
    },
    {
      day: 'Thu 01',
      safe: '5',
      violations: '0',
      status: 'Met',
    },
    {
      day: 'Fri 02',
      safe: '5',
      violations: '0',
      status: 'Met',
    },
    {
      day: 'Mon 04',
      safe: '5',
      violations: '0',
      status: 'Met',
    },
  ],

  // ====================================================
  // Monthly Compliance Heatmap
  // ====================================================

  heatmapData: {
    header: {
      period: 'June–July 2026',
      subtitle: 'Gate-by-gate daily history',
    },

    rows: [
      {
        gate: 'Login',

        days: [
          {
            day: 1,
            workDate: '2026-06-01',
            status: 'Met',
          },
          {
            day: 2,
            workDate: '2026-06-02',
            status: 'Met',
          },
          {
            day: 3,
            workDate: '2026-06-03',
            status: 'Missed',
          },
          {
            day: 4,
            workDate: '2026-06-04',
            status: 'Met',
          },
          {
            day: 5,
            workDate: '2026-06-05',
            status: 'Met',
          },
          {
            day: 6,
            workDate: '2026-06-06',
            status: 'Met',
          },
          {
            day: 7,
            workDate: '2026-06-07',
            status: 'Met',
          },
          {
            day: 8,
            workDate: '2026-06-08',
            status: 'Met',
          },
          {
            day: 9,
            workDate: '2026-06-09',
            status: 'Missed',
          },
          {
            day: 10,
            workDate: '2026-06-10',
            status: 'Met',
          },
          {
            day: 11,
            workDate: '2026-06-11',
            status: 'Met',
          },
          {
            day: 12,
            workDate: '2026-06-12',
            status: 'Met',
          },
          {
            day: 13,
            workDate: '2026-06-13',
            status: 'Met',
          },
          {
            day: 14,
            workDate: '2026-06-14',
            status: 'Missed',
          },
          {
            day: 15,
            workDate: '2026-06-15',
            status: 'Met',
          },
          {
            day: 16,
            workDate: '2026-06-16',
            status: 'Met',
          },
          {
            day: 17,
            workDate: '2026-06-17',
            status: 'Met',
          },
          {
            day: 18,
            workDate: '2026-06-18',
            status: 'Met',
          },
          {
            day: 19,
            workDate: '2026-06-19',
            status: 'Met',
          },
          {
            day: 20,
            workDate: '2026-06-20',
            status: 'Met',
          },
        ],
      },

      {
        gate: 'Breaks',

        days: [
          {
            day: 1,
            workDate: '2026-06-01',
            status: 'Met',
          },
          {
            day: 2,
            workDate: '2026-06-02',
            status: 'Met',
          },
          {
            day: 3,
            workDate: '2026-06-03',
            status: 'Met',
          },
          {
            day: 4,
            workDate: '2026-06-04',
            status: 'Met',
          },
          {
            day: 5,
            workDate: '2026-06-05',
            status: 'Missed',
          },
          {
            day: 6,
            workDate: '2026-06-06',
            status: 'Met',
          },
          {
            day: 7,
            workDate: '2026-06-07',
            status: 'Met',
          },
          {
            day: 8,
            workDate: '2026-06-08',
            status: 'Met',
          },
          {
            day: 9,
            workDate: '2026-06-09',
            status: 'Met',
          },
          {
            day: 10,
            workDate: '2026-06-10',
            status: 'Met',
          },
          {
            day: 11,
            workDate: '2026-06-11',
            status: 'Met',
          },
          {
            day: 12,
            workDate: '2026-06-12',
            status: 'Missed',
          },
          {
            day: 13,
            workDate: '2026-06-13',
            status: 'Met',
          },
          {
            day: 14,
            workDate: '2026-06-14',
            status: 'Met',
          },
          {
            day: 15,
            workDate: '2026-06-15',
            status: 'Met',
          },
          {
            day: 16,
            workDate: '2026-06-16',
            status: 'Met',
          },
          {
            day: 17,
            workDate: '2026-06-17',
            status: 'Met',
          },
          {
            day: 18,
            workDate: '2026-06-18',
            status: 'Met',
          },
          {
            day: 19,
            workDate: '2026-06-19',
            status: 'Missed',
          },
          {
            day: 20,
            workDate: '2026-06-20',
            status: 'Met',
          },
        ],
      },

      {
        gate: 'Hours',

        days: [
          {
            day: 1,
            workDate: '2026-06-01',
            status: 'Met',
          },
          {
            day: 2,
            workDate: '2026-06-02',
            status: 'Met',
          },
          {
            day: 3,
            workDate: '2026-06-03',
            status: 'Met',
          },
          {
            day: 4,
            workDate: '2026-06-04',
            status: 'Met',
          },
          {
            day: 5,
            workDate: '2026-06-05',
            status: 'Met',
          },
          {
            day: 6,
            workDate: '2026-06-06',
            status: 'Met',
          },
          {
            day: 7,
            workDate: '2026-06-07',
            status: 'Missed',
          },
          {
            day: 8,
            workDate: '2026-06-08',
            status: 'Met',
          },
          {
            day: 9,
            workDate: '2026-06-09',
            status: 'Met',
          },
          {
            day: 10,
            workDate: '2026-06-10',
            status: 'Met',
          },
          {
            day: 11,
            workDate: '2026-06-11',
            status: 'Met',
          },
          {
            day: 12,
            workDate: '2026-06-12',
            status: 'Met',
          },
          {
            day: 13,
            workDate: '2026-06-13',
            status: 'Met',
          },
          {
            day: 14,
            workDate: '2026-06-14',
            status: 'Met',
          },
          {
            day: 15,
            workDate: '2026-06-15',
            status: 'Met',
          },
          {
            day: 16,
            workDate: '2026-06-16',
            status: 'Missed',
          },
          {
            day: 17,
            workDate: '2026-06-17',
            status: 'Met',
          },
          {
            day: 18,
            workDate: '2026-06-18',
            status: 'Met',
          },
          {
            day: 19,
            workDate: '2026-06-19',
            status: 'Met',
          },
          {
            day: 20,
            workDate: '2026-06-20',
            status: 'Met',
          },
        ],
      },

      {
        gate: 'Policy',

        days: [
          {
            day: 1,
            workDate: '2026-06-01',
            status: 'Met',
          },
          {
            day: 2,
            workDate: '2026-06-02',
            status: 'Met',
          },
          {
            day: 3,
            workDate: '2026-06-03',
            status: 'Met',
          },
          {
            day: 4,
            workDate: '2026-06-04',
            status: 'Missed',
          },
          {
            day: 5,
            workDate: '2026-06-05',
            status: 'Met',
          },
          {
            day: 6,
            workDate: '2026-06-06',
            status: 'Met',
          },
          {
            day: 7,
            workDate: '2026-06-07',
            status: 'Met',
          },
          {
            day: 8,
            workDate: '2026-06-08',
            status: 'Met',
          },
          {
            day: 9,
            workDate: '2026-06-09',
            status: 'Met',
          },
          {
            day: 10,
            workDate: '2026-06-10',
            status: 'Met',
          },
          {
            day: 11,
            workDate: '2026-06-11',
            status: 'Missed',
          },
          {
            day: 12,
            workDate: '2026-06-12',
            status: 'Met',
          },
          {
            day: 13,
            workDate: '2026-06-13',
            status: 'Met',
          },
          {
            day: 14,
            workDate: '2026-06-14',
            status: 'Met',
          },
          {
            day: 15,
            workDate: '2026-06-15',
            status: 'Met',
          },
          {
            day: 16,
            workDate: '2026-06-16',
            status: 'Met',
          },
          {
            day: 17,
            workDate: '2026-06-17',
            status: 'Met',
          },
          {
            day: 18,
            workDate: '2026-06-18',
            status: 'Met',
          },
          {
            day: 19,
            workDate: '2026-06-19',
            status: 'Met',
          },
          {
            day: 20,
            workDate: '2026-06-20',
            status: 'Met',
          },
        ],
      },
    ],
  },
};